import React from 'react';

import { formatRelativeDateTime } from 'utils/dateUtils';

import { DealRole } from '__generated__/globalTypes';

import { BidData, getBidDescription, formatBidPrice, getOwnershipBadge } from './shared/bidUtils';

interface ReadOnlyBidHistoryProps {
  bids: BidData[];
  userRole: DealRole;
  selectedBidId?: string;
  onBidSelect?: (bidId: string) => void;
}

export const ReadOnlyBidHistory: React.FC<ReadOnlyBidHistoryProps> = ({
  bids,
  userRole,
  selectedBidId,
  onBidSelect,
}) => {
  if (!bids || bids.length === 0) {
    return <div className="py-8 text-center text-gray-400">No bidding history available.</div>;
  }

  // Sort bids chronologically
  const sortedBids = [...bids].sort(
    (a, b) => new Date(a.created_date).getTime() - new Date(b.created_date).getTime(),
  );

  const statusStyles = {
    ACCEPTED: 'bg-green-600 text-green-100',
    REJECTED: 'bg-red-600 text-red-100',
  };

  return (
    <div className="w-full text-white">
      <div className="mb-4 rounded-lg border border-gray-700 bg-gray-800/30 px-4 py-3 text-center">
        <span className="text-sm text-gray-300">Bidding completed. Historical view only.</span>
      </div>

      <ul className="space-y-1">
        {sortedBids.map((bid, index) => {
          const bidDescription = getBidDescription(bid);
          const priceDisplay = formatBidPrice(bid);
          const ownershipBadge = getOwnershipBadge(bid);
          const display = formatRelativeDateTime(bid.created_date);
          const isYourBid =
            (userRole === DealRole.BUYER && bid.bidder === DealRole.BUYER) ||
            (userRole === DealRole.SELLER && bid.bidder === DealRole.SELLER);
          const isSelected = selectedBidId === bid.bid_id;

          return (
            <li
              key={bid.bid_id}
              onClick={() => onBidSelect?.(bid.bid_id)}
              className={`grid grid-cols-[500px_250px_150px_1fr] items-center justify-between border-l py-2 pl-3 transition-colors ${
                isSelected
                  ? 'border-blue-500 bg-blue-900/20'
                  : 'border-gray-800 hover:bg-gray-800/50'
              } ${onBidSelect ? 'cursor-pointer' : ''}`}
            >
              {/* Bid Description */}
              <div className="flex items-center gap-2">
                <span className="text-l font-medium text-white">
                  {bid.bidder === DealRole.BUYER
                    ? `Bid on ${bidDescription}`
                    : `Counteroffer on ${bidDescription}`}
                  {isYourBid && <span className="ml-2 text-xs text-blue-400">(You)</span>}
                </span>
                <span className="ml-2 text-xs text-gray-500">#{index + 1}</span>
              </div>

              {/* Price and Ownership */}
              <div className="flex items-center gap-3">
                <span className="text-l tracking-tight">Price</span>
                <span className="text-l text-gray-400">{priceDisplay}</span>
                {ownershipBadge && (
                  <span className="rounded bg-purple-800 px-2 py-0.5 text-xs text-purple-200">
                    {ownershipBadge}
                  </span>
                )}
              </div>

              {/* Status */}
              <div className="flex items-center">
                {(bid.status === 'ACCEPTED' || bid.status === 'REJECTED') && (
                  <span
                    className={`rounded px-2 py-1 text-xs font-semibold ${statusStyles[bid.status as keyof typeof statusStyles]}`}
                  >
                    {bid.status}
                  </span>
                )}
              </div>

              {/* Date */}
              <div className="flex items-center justify-end">
                <span className="min-w-[240px] text-sm text-gray-400">{display}</span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
